import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { useDeviceInfo } from "@swa-ui/browser";
import { Heading, Icon, ListMatrix } from "@swa-ui/core";

import { backgroundPropType, getBackgroundStyle } from "../Banner/BannerUtils";
import { CallToAction } from "../CallToAction";
import { HtmlValue } from "../HtmlValue";
import styles from "./BannerGridLayout1.module.scss";
import { callToActionPropType } from "./utils";

const BannerGridLayout1 = (props) => {
  const { background, callToAction, items, title } = props;
  const { screenSize } = useDeviceInfo();
  const [animationToken, setAnimationToken] = useState();

  // TODO: Remove the animation token in ATLASMON-1395 after PHX-2544 is completed
  useEffect(() => {
    setAnimationToken(`banner-grid-layout-1-${screenSize}`);
  }, [screenSize]);

  return (
    <div className={styles.container} style={getBackgroundStyle(background)}>
      {renderHeading()}
      <ListMatrix {...getListMatrixProps()} />
      {renderCallToAction()}
    </div>
  );

  function renderCallToAction() {
    return callToAction && <CallToAction {...getCallToActionProps()} />;
  }

  function renderText(text) {
    return (
      <div style={{ ...getTextProps(text) }}>
        <HtmlValue htmlValue={text?.value} />
      </div>
    );
  }

  function renderHeading() {
    return title && <Heading {...getHeadingProps()} />;
  }

  function getHeadingProps() {
    return {
      children: <span {...getHeadingStyleProps(title)}>{title.value}</span>,
      className: styles.heading,
      headingLevel: 2,
      styleLevel: 2,
    };
  }

  function getHeadingStyleProps(styleProps) {
    const { color, fontSize, fontWeight } = styleProps ?? {};

    return {
      style: {
        color,
        fontSize,
        fontWeight,
      },
    };
  }

  function getListMatrixProps() {
    const columnSizeMap = {
      large: 4,
      medium: 2,
      small: 1,
      xlarge: 4,
    };

    return {
      animationToken,
      items: getItems(),
      layout: "grid",
      numberOfColumns: columnSizeMap[screenSize],
    };
  }

  function getItems() {
    return items.map((item) => {
      const { text } = item;
      const { primary, secondary } = text || {};
      const { prefixIcon } = primary || {};

      return {
        content: (
          <div className={styles.itemContainer} key={text}>
            <div className={styles.icon}>
              {prefixIcon?.name && <Icon {...getIconProps(prefixIcon)} />}
            </div>
            <div className={styles.primaryText}>{renderText(primary)}</div>
            {secondary && <div className={styles.secondaryText}>{renderText(secondary)}</div>}
          </div>
        ),
      };
    });
  }

  function getIconProps(prefixIcon) {
    const { color, name, size } = prefixIcon;

    return {
      "aria-label": name,
      color,
      name,
      size,
    };
  }

  function getTextProps(text) {
    const { color, fontSize, fontWeight } = text;

    return {
      color,
      fontSize,
      fontWeight,
    };
  }

  function getCallToActionProps() {
    return {
      additionalProps: {
        fullWidth: screenSize === "small",
      },
      className: styles.callToAction,
      ...callToAction,
    };
  }
};

BannerGridLayout1.propTypes = {
  /** Background configuration. */
  background: backgroundPropType,

  /** CallToAction link/button to be displayed. */
  callToAction: callToActionPropType,

  /** Banner text content. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.shape({
        primary: PropTypes.shape({
          prefixIcon: PropTypes.shape({
            color: PropTypes.string,
            name: PropTypes.string,
            size: PropTypes.string,
          }),
        }),
        secondary: PropTypes.object,
      }),
    })
  ).isRequired,

  /** Optional title for the banner. */
  title: PropTypes.shape({
    value: PropTypes.string,
  }),
};

export default BannerGridLayout1;
